import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SeparatedDocument, SeparatedDocPage } from '../../models/file-review.model';
import { FileReviewService } from '../../services/file-review.service';
import { ToasterService } from '../../../shared/services/toaster/toaster.service';
import { MultipartDocumentStatus } from '../../models/mutipart-document-status.enum';

@Component({
  selector: 'app-page-gallery',
  templateUrl: './page-gallery.component.html',
  styleUrls: ['./page-gallery.component.scss']
})
export class PageGalleryComponent implements OnChanges {

  @Input() selected_document: SeparatedDocument | null = null;
  @Input() file_name: string = '';
  @Input() file_id: string = '';
  @Input() allDocuments: SeparatedDocument[] = [];
  @Input() fileStatus: string = '';

  @Output() approveDocument = new EventEmitter<string>();
  @Output() unapproveDocument = new EventEmitter<string>();
  @Output() reorderPages = new EventEmitter<{ doc_id: string, previousIndex: number, currentIndex: number }>();
  @Output() movePageToDocument = new EventEmitter<{ page_id: string, target_doc_id: string }>();

  // Expose enum for template
  MultipartDocumentStatus = MultipartDocumentStatus;

  isEditingTitle: boolean = false;
  editedTitle: string = '';
  
  // Loading states
  isReordering: boolean = false;
  movingPageIds: Set<string> = new Set();
  
  // Cache for loaded presigned URLs
  private pageUrlCache: Map<string, { url: string; expiresAt: number }> = new Map();

  constructor(
    private sanitizer: DomSanitizer,
    private fileReviewService: FileReviewService,
    private toasterService: ToasterService
  ) {}

  /**
   * Lifecycle hook - detects when selected document changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selected_document'] && this.selected_document) {
      // Load presigned URLs for all pages in the selected document
      this.loadPageUrls();
    }
  }

  /**
   * Loads presigned URLs for all pages in the current document
   */
  private async loadPageUrls(): Promise<void> {
    if (!this.selected_document || !this.file_id) {
      return;
    }

    // Load URLs for all pages in parallel
    const loadPromises = this.selected_document.pages.map(page => 
      this.loadPageUrl(page)
    );

    await Promise.all(loadPromises);
  }

  /**
   * Loads presigned URL for a single page
   */
  private async loadPageUrl(page: SeparatedDocPage): Promise<void> {
    try {
      // Check cache first
      const cached = this.pageUrlCache.get(page.page_id);
      if (cached && cached.expiresAt > Date.now()) {
        page.preview_url = cached.url;
        return;
      }

      // Fetch new presigned URL
      const result = await this.fileReviewService.getPageAccess(
        this.file_id,
        page.page_id,
        'view'
      );

      // Update page URL
      page.preview_url = result.presignedUrl;

      // Cache with expiration (expiresIn is in seconds)
      const expiresAt = Date.now() + (result.expiresIn * 1000);
      this.pageUrlCache.set(page.page_id, {
        url: result.presignedUrl,
        expiresAt: expiresAt
      });
    } catch (error) {
      page.preview_url = ''; // Set empty to show placeholder
    }
  }

  /**
   * Handles drag and drop page reordering
   * @param event - The CDK drag drop event
   */
  onPageDrop(event: CdkDragDrop<SeparatedDocPage[]>): void {
    // Check if it's the same container (reordering within same document)
    if (event.previousContainer === event.container) {
      if (this.selected_document && event.previousIndex !== event.currentIndex) {
        // Show loading toaster
        this.isReordering = true;
        this.toasterService.info('Reordering pages...');
        
        // Emit the reorder request (parent will handle API call and update)
        this.reorderPages.emit({
          doc_id: this.selected_document.doc_id,
          previousIndex: event.previousIndex,
          currentIndex: event.currentIndex
        });
      }
    }
  }

  /**
   * Handles the approval and locking of the current document
   */
  onApproveDocument(): void {
    if (this.selected_document) {
      this.approveDocument.emit(this.selected_document.doc_id);
    }
  }

  /**
   * Handles unapproving the current document
   */
  onUnapproveDocument(): void {
    if (this.selected_document) {
      this.unapproveDocument.emit(this.selected_document.doc_id);
    }
  }



  /**
   * Handles moving a page to a different document
   * @param page_id - The ID of the page to move
   * @param target_doc_id - The ID of the target document
   */
  onMovePageToDocument(page_id: string, target_doc_id: string): void {
    // Show toaster message
    this.toasterService.info('Moving page...');
    
    // Add page to loading state (will show gray overlay)
    this.movingPageIds.add(page_id);
    
    this.movePageToDocument.emit({ page_id, target_doc_id });
  }

  /**
   * Gets all documents except the currently selected one
   * @returns Array of documents excluding the selected document
   */
  getOtherDocuments(): SeparatedDocument[] {
    if (!this.selected_document) {
      return this.allDocuments;
    }
    return this.allDocuments.filter(doc => doc.doc_id !== this.selected_document!.doc_id);
  }

  /**
   * Handles file selection change from dropdown
   * @param event - The select change event
   * @param page_id - The ID of the page to move
   */
  onMovePageToFile(event: Event, page_id: string): void {
    const target = event.target as HTMLSelectElement;
    const target_doc_id = target.value;
    
    if (target_doc_id && this.selected_document && target_doc_id !== this.selected_document.doc_id) {
      // Show toaster message
      this.toasterService.info('Moving page...');
      
      // Add page to loading state (will show gray overlay)
      this.movingPageIds.add(page_id);
      
      this.movePageToDocument.emit({ page_id, target_doc_id });
      // Reset select to current document
      target.value = this.selected_document.doc_id;
    }
  }

  /**
   * Opens a preview modal for a specific page
   * @param page - The page to preview
   */
  previewPage(page: SeparatedDocPage): void {
    // TODO: Implement preview modal
  }

  /**
   * Checks if the URL points to a PDF file
   * @param url - The URL to check
   * @returns True if the URL is a PDF
   */
  isPdfUrl(url: string): boolean {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf');
  }

  /**
   * Sanitizes a URL for use in an iframe
   * @param url - The URL to sanitize
   * @returns Sanitized URL safe for iframe src
   */
  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  /**
   * Handles image load errors
   * @param event - The error event
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  /**
   * Formats page ID by removing all leading zeros
   * Examples: "001" → "1", "010" → "10", "100" → "100"
   * @param pageId - The page ID to format
   * @returns Formatted page ID
   */
  formatPageId(pageId: string): string {
    if (!pageId) return pageId;
    
    // Convert to number to remove all leading zeros
    const num = parseInt(pageId, 10);
    return num.toString(); // 001 → 1, 010 → 10, 100 → 100
  }

  /**
   * Starts editing the document title
   */
  startEditingTitle(): void {
    if (this.selected_document) {
      this.isEditingTitle = true;
      this.editedTitle = this.selected_document.display_name;
      
      // Focus the input after the view updates
      setTimeout(() => {
        const input = document.querySelector('.title-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 0);
    }
  }

  /**
   * Saves the edited document title (local only, no API call)
   */
  saveTitle(): void {
    if (!this.selected_document || !this.editedTitle.trim()) {
      this.cancelEditTitle();
      return;
    }

    const trimmedTitle = this.editedTitle.trim();
    
    // Update display name locally only (no API call)
    if (trimmedTitle !== this.selected_document.display_name) {
      this.selected_document.display_name = trimmedTitle;
    }
    
    this.isEditingTitle = false;
  }

  /**
   * Cancels title editing and reverts to original
   */
  cancelEditTitle(): void {
    this.isEditingTitle = false;
    this.editedTitle = '';
  }

  /**
   * Notifies component that reordering operation is complete
   * Called by parent after successful API call
   * Note: Success toast is shown by parent component
   */
  onReorderComplete(): void {
    this.isReordering = false;
  }

  /**
   * Notifies component that page move operation is complete
   * Called by parent after successful API call
   * Note: Success toast is shown by parent component
   * @param page_id - The ID of the page that was moved
   */
  onMoveComplete(page_id: string): void {
    this.movingPageIds.delete(page_id);
  }

  /**
   * Checks if a page is currently being moved
   * @param page_id - The ID of the page to check
   * @returns True if the page is being moved
   */
  isPageMoving(page_id: string): boolean {
    return this.movingPageIds.has(page_id);
  }

  /**
   * Handles the approve button click
   * Emits the approveDocument event with the current document ID
   */
  onApproveButtonClick(): void {
    if (this.selected_document) {
      this.approveDocument.emit(this.selected_document.doc_id);
    }
  }

}
